import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card } from "../../components/ui/Card";

export function VerifyPhonePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((s) => s.setAuth);

  const phoneFromParams = searchParams.get("phone") || "";

  const [phone, setPhone] = useState(phoneFromParams);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const handleVerify = async (otp: string, phoneNumber: string) => {
    if (!phoneNumber || !otp) return;
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.verifyPhone(phoneNumber, otp);
      if (!response.success) {
        setError(response.error || t("auth.verificationFailedError"));
        return;
      }
      if (response.token) {
        setAuth(response.token, response.user, response.organization, response.businesses, undefined, undefined);
        await useAuthStore.getState().resolveBusinessAndOutlet();
        navigate("/dashboard", { replace: true });
      } else if (response.mfa_required && response.mfa_token) {
        // Two-step is on (#159): straight to the code, not the password again.
        navigate("/login", { replace: true, state: { mfaToken: response.mfa_token } });
      } else {
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!phone) {
      setError(t("auth.enterPhoneFirstError"));
      return;
    }
    setResendMessage(null);
    setError(null);
    setResendLoading(true);
    try {
      const result = await authApi.resendPhoneOTP(phone);
      setResendMessage(result.message || t("auth.newPhoneCodeSent"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-4">
      <Card className="w-full max-w-md" title={t("auth.verifyYourPhone")}>
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            {t("auth.enterPhoneOtpDescription")}
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          {resendMessage && (
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              {resendMessage}
            </div>
          )}

          <Input
            label={t("auth.phoneNumberLabel")}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 9876543210"
            disabled={!!phoneFromParams}
          />

          <Input
            label={t("auth.verificationCode")}
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            maxLength={6}
          />

          <Button
            type="button"
            className="w-full"
            loading={loading}
            onClick={() => handleVerify(code, phone)}
            disabled={code.length !== 6 || !phone}
          >
            {t("auth.verifyPhone")}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="text-brand hover:underline disabled:opacity-50"
            >
              {resendLoading ? t("auth.sending") : t("auth.resendCode")}
            </button>
            <Link to="/login" className="text-text-muted hover:underline">
              {t("auth.backToLogin")}
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
